/**
 * "Is this table actually usable by the player?"
 *
 * The client's only definition of hydrated used to be `!!game.tabletype`, which
 * answers a narrower question: did ANY snapshot arrive. EN-10324 shipped a
 * loading indicator and a terminal error modal on top of that signal, and a
 * table reported from the field then showed the gap — a felt fully drawn
 * (table no, blinds, pot, dealer button) with all nine seats reading "Open"
 * while a hand was live. `tabletype` was set, so every loading and recovery
 * path stood down and the player was left with a table they could not use and
 * no way out of it.
 *
 * WHY THAT SHAPE EXISTS. Seat occupancy reaches the client through a different
 * table-row column family than everything else on the felt:
 *
 *   - Rendered seats come from `p{i}data`. The broadcaster
 *     (cash-game-broadcast/lib/gameTransforms.js `getTablePlayers`) builds
 *     `const player = {}` per seat and fills it ONLY `if (table["p"+i+"data"])`,
 *     then assigns `tablePlayers[i] = player`. An empty `p{i}data` therefore
 *     ships a bare `{}` placeholder, and components/Seat renders `OpenSeat`
 *     for any seat without an `ID`.
 *   - Every other felt readout — and, crucially, the `gameState` player counts
 *     (`shared/games/common/game.js` `getTablePlayerStats`) — comes from
 *     `p{i}name`.
 *
 * So the payload carries TWO independently derived views of the same fact, and
 * comparing them detects the broken shape without inventing a heuristic: the
 * server says N players are seated, and the client was given the seat data for
 * none of them.
 *
 * Deliberately NOT based on pot or hand phase. `game.gameNo` (the felt's "HAND"
 * readout) is a persistent hand COUNTER column present on every snapshot, not a
 * live-hand flag, and a non-zero pot can survive between hands — either would
 * false-positive on a table that is merely idle.
 */

/**
 * Seats the client can actually draw a player in, i.e. the `p{i}data` view.
 *
 * `players` is 1-indexed with a hole at 0 (the broadcaster writes
 * `tablePlayers[i] = player` for seats 1..seats), which is why the felt reads
 * it as `players.slice(1)`. A vacant seat is still PRESENT: `getTablePlayers`
 * takes an else branch writing the full key set with empty values (`ID: ""`,
 * `GUID: ""`, `stack: 0`, …). `ID` is the gate components/Seat uses to choose
 * OpenSeat, and `""` is falsy, so counting on `seat.ID` matches what the felt
 * actually draws.
 */
export const renderedSeatCount = (players: unknown): number => {
  if (!Array.isArray(players)) return 0;
  return players.slice(1).filter((seat: any) => seat && seat.ID).length;
};

/**
 * Seats the SERVER says are occupied, i.e. the `p{i}name` view — or `null` when
 * the payload carries no such evidence.
 *
 * `null` is not zero. A missing or non-numeric count means this cross-check has
 * nothing to say about the table, and callers must treat it as "no evidence"
 * rather than as either "no players, table fine" or "broken". Every producer
 * that can actually hydrate a felt does send it (see hasSeatDataGap), so this
 * is a safety net for partial or future payloads, not an expected path.
 */
export const reportedSeatedCount = (gameState: unknown): number | null => {
  const raw = (gameState as any)?.noPlayers;
  // Only a number or a numeric string counts as evidence. Coercing blindly
  // would turn `null`, `''` and `false` into 0 — i.e. silently assert "nobody
  // is seated, the table is fine" from a payload that said nothing at all.
  if (typeof raw !== 'number' && typeof raw !== 'string') return null;
  if (typeof raw === 'string' && raw.trim() === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
};

/**
 * The broken shape: the server reports seated players, and not one of them
 * arrived with the seat data needed to draw them.
 *
 * VERIFIED PRODUCERS of `gameState.noPlayers` — every path that can hydrate a
 * felt ships it, so the cross-check is live wherever the felt is:
 *   - serverless-v2 services/cash-game-broadcast/lib/gameTransforms.js (the v2
 *     path; handles cash AND tournament tables — it branches on isMTT
 *     internally and the handler applies no table-type filter)
 *   - serverless/lambdas/broadcasters/table/state-updates/index.js (the legacy
 *     path the v2 transform was copied from)
 *   - apps/engine texas/socket.js and omaha/socket.js (the legacy engine)
 * tournament-broadcast emits no table state at all (only active tables, prizes,
 * ranking and details), so tournament felts come from the same transforms above.
 *
 * ONLY A FULL-LENGTH SEAT ARRAY COUNTS AS EVIDENCE. The broken shape is always
 * "the server sent a row for every seat and populated none of them". An empty
 * or absent collection is never that:
 *
 *   - `clearTablePlayers` (reducers/currentTable.tsx) sets `players = []` as a
 *     deliberate CLIENT wipe, and App.tsx dispatches it for every closed table
 *     — including ones it keeps on screen behind the "table closed" prompt.
 *     It leaves `gameState.noPlayers` stale at >0, and a closed table stops
 *     emitting gotTexas, so nothing repairs either side. Without this guard
 *     that is a permanent false gap on a table behaving exactly as designed,
 *     escalating through the reconnect gate to a fleet-wide socket teardown and
 *     an error modal over correct messaging.
 *   - An absent collection is a partial payload, which the reducer's own guard
 *     (mergeArrayItems) already handles by keeping the last known-good seats.
 *
 * DEGRADES SAFELY. Returns false — "nothing to report" — when there is no
 * count, when the collection is missing, empty or hole-only, or when any seat
 * did render. It can only ever ADD a broken signal to the existing
 * `!!tabletype` judgement, never clear one.
 *
 * Partial-view note: a seat SITTING OUT still has `p{i}data`, so it renders and
 * counts here. A genuinely empty table reports 0 and is not flagged. Both are
 * covered by tests.
 */
export const hasSeatDataGap = (players: unknown, gameState: unknown): boolean =>
  isSeatDataGap(
    Array.isArray(players) ? players.length : 0,
    renderedSeatCount(players),
    reportedSeatedCount(gameState)
  );

/**
 * The same decision over ALREADY-NARROWED values.
 *
 * This is the form `useTableUsable` uses. Selecting `table.players` and
 * `table.gameState` wholesale in a redux selector re-renders the felt on every
 * push (those objects change identity constantly during a hand — see the note
 * in the hook), so the hook narrows each slice to a scalar inside its selector
 * and decides from those. Keeping ONE implementation means the narrowed path
 * and the object path can never drift; `hasSeatDataGap` above is the
 * convenience wrapper the tests and any object-holding caller use.
 *
 * @param seatArrayLength raw length of the seat collection, index-0 hole included
 * @param drawnSeats      seats actually carrying data (renderedSeatCount)
 * @param reportedSeated  the server's own count, or null for "no evidence"
 */
export const isSeatDataGap = (
  seatArrayLength: number,
  drawnSeats: number,
  reportedSeated: number | null
): boolean => {
  if (reportedSeated === null || reportedSeated === 0) return false;
  // Index 0 is the broadcaster's hole, so a real seat array is always longer
  // than 1. `<= 1` covers [], [null] and any non-array (length reported as 0).
  if (seatArrayLength <= 1) return false;
  return drawnSeats === 0;
};
